import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, Instagram, MapPin } from "lucide-react";
import { BRAND, BERMUDA_PARISHES } from "@/lib/site";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead, Field, Input, Textarea, SubmitButton, CtaBand, usePageMeta } from "@/components/shared";
import { submitLead } from "@/lib/api";

export default function Contact() {
  usePageMeta("Contact", "Contact The Bermuda Co-Host — Bermuda vacation rental management, co-hosting, cleaning and property care. Call +1 (441) 519-1794.");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead("contact", form);
      toast.success("Message received — we'll reply shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <section className="mx-auto max-w-[1440px] px-6 pb-24 pt-40 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
          <div>
            <SectionHead
              overline="Contact"
              title={<>Let's talk about<br /><span className="italic text-bermuda-dark">your property.</span></>}
              copy="Call, email or send a message — a real local person answers, and every enquiry receives a thoughtful reply."
            />
            <div className="mt-12 space-y-5">
              {[
                [Phone, BRAND.phone, BRAND.phoneHref, "contact-phone"],
                [Mail, BRAND.email, `mailto:${BRAND.email}`, "contact-email"],
                [Instagram, "@thebermudacohost", BRAND.instagram, "contact-instagram"],
              ].map(([Icon, label, href, testid]) => (
                <Reveal key={testid}>
                  <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={testid} className="group flex items-center gap-5 border border-navy/10 bg-white p-6 transition-all duration-300 hover:border-bermuda hover:shadow-lg hover:shadow-navy/5">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-pink-soft text-bermuda-dark transition-colors group-hover:bg-bermuda group-hover:text-navy">
                      <Icon size={17} strokeWidth={1.5} />
                    </span>
                    <span className="text-sm font-medium text-navy">{label}</span>
                  </a>
                </Reveal>
              ))}
              <Reveal>
                <div className="flex items-start gap-5 bg-sand-light p-6">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean">
                    <MapPin size={17} strokeWidth={1.5} />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-navy">Serving homeowners island-wide</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-navy/55">{BERMUDA_PARISHES.join(" · ")}</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.15}>
            <div className="border border-navy/10 bg-white p-8 shadow-xl shadow-navy/5 lg:p-10">
              <h3 className="font-serif text-2xl text-navy">Send a Message</h3>
              <form onSubmit={onSubmit} className="mt-7 grid gap-5" data-testid="contact-form">
                <Field label="Full Name" required><Input data-testid="contact-name" required value={form.name} onChange={set("name")} /></Field>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Email" required><Input data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} /></Field>
                  <Field label="Phone"><Input data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} /></Field>
                </div>
                <Field label="How Can We Help?" required><Textarea data-testid="contact-message" required value={form.message} onChange={set("message")} placeholder="Tell us about your property and what you need…" /></Field>
                <SubmitButton loading={loading} testid="contact-submit">Send Message</SubmitButton>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
      <CtaBand title="Ready for the full picture?" copy="The property assessment is the most useful twenty minutes you will spend on your rental this year." />
    </main>
  );
}
